import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  TravelReminderTypeEnum,
  TravelReminderTypeEnumMapping
} from '../../../lib/types/enums/travel/travel-reminder-type.enum'
import {
  TransferReminder,
  TransferReminderInput
} from '../../../lib/types/models/transfer/transfer-reminder'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../lib/utils/functions/misc.functions'
import { createDateStringSchema } from '../../../lib/utils/validations/date.validation'
import { createEnumSchema } from '../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../lib/utils/validations/int-array-validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../elements/validation-buttons'

interface TransferReminderFormProps extends BaseFormProps<TransferReminder> {
  className?: string
}

const formSchema: ZodType<TransferReminderInput> = z.object({
  type: createEnumSchema({ fieldName: 'un type de rappel', enumType: TravelReminderTypeEnum }),
  deadline: createDateStringSchema({
    inputName: 'Date limite',
    minDate: DateFormatter.toDateFormatted(new Date())
  }),
  daysBeforeDeadline: createNumberSchema('Jours avant la date limite', {
    minimumValue: 1
  }),
  comments: createStringSchema('Commentaire', { nullish: true })
})

const TransferReminderForm = ({
  defaultValues,
  className,
  ...validationButtonProps
}: TransferReminderFormProps) => {
  const form = useForm<TransferReminder>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn(className, 'flex flex-col gap-3')}>
          <FormField
            control={form.control}
            name={'type'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledSelectInput<TravelReminderTypeEnum>
                    {...field}
                    required
                    options={Object.values(TravelReminderTypeEnum).map((type) => ({
                      label: TravelReminderTypeEnumMapping[type],
                      value: type
                    }))}
                    label={'Type de rappel'}
                    selectedOption={getSelectedEnumOption(
                      field.value,
                      TravelReminderTypeEnumMapping
                    )}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'deadline'}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput {...field} required mode="over" label="Date limite" />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'daysBeforeDeadline'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type="number"
                    label={'Jours avant la date limite'}
                    placeholder={'E.g. 7'}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'comments'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledInput
                    {...field}
                    type="textarea"
                    label={'Commentaire'}
                    placeholder={'Commentaire...'}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default TransferReminderForm
